import React, { useEffect, useMemo, useCallback, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./styles/ViewOrders.css";

import product1 from "../images/products/stamp.jpg";
import { base_url } from "../utils/axiosConfig";
import { getOrders, cancelMyOrder } from "../app/features/user/userSlice";

/* =========================
   HELPERS
========================= */
const formatUGX = (n) => `UGX ${Number(n || 0).toLocaleString()}`;
const safeText = (v) => String(v ?? "").trim();

const normalizeImg = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  return val?.url || "";
};

const getProductPath = (slugOrId) => `/product/${encodeURIComponent(String(slugOrId || ""))}`;

const getFirstProductImage = (p) => {
  if (!p) return "";
  if (Array.isArray(p?.images) && p.images.length) return normalizeImg(p.images[0]);
  return normalizeImg(p?.image);
};

const getOrderItemSlugOrId = (item) => {
  const prodObj =
    item?.productSnapshot && typeof item.productSnapshot === "object"
      ? item.productSnapshot
      : item?.product && typeof item.product === "object"
      ? item.product
      : null;

  const slug =
    safeText(prodObj?.slug) ||
    safeText(item?.productSlug) ||
    safeText(item?.slug);

  if (slug) return slug;

  const id =
    prodObj?._id ||
    prodObj?.id ||
    item?.productId ||
    item?.product?._id ||
    item?.product ||
    item?.id ||
    "";

  return safeText(id);
};

const pickOrderItemImage = (item) => {
  const fromVariant = normalizeImg(item?.variantImage);
  if (fromVariant) return fromVariant;

  const prodObj =
    item?.productSnapshot && typeof item.productSnapshot === "object"
      ? item.productSnapshot
      : item?.product && typeof item.product === "object"
      ? item.product
      : null;

  const fromProd = getFirstProductImage(prodObj);
  return fromProd || product1;
};

const ViewOrders = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id: orderParamId } = useParams();
  const location = useLocation();

  const qsOrderId = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get("id") || "";
  }, [location.search]);

  const stateOrderId = safeText(location?.state?.selectedOrderId || "");
  const orderId = safeText(orderParamId || qsOrderId || stateOrderId);

  const authState = useSelector((state) => state?.auth || {});
  const user = authState?.user || null;

  const isLoggedIn = Boolean(user?.token || localStorage.getItem("token"));
  const isLoading = Boolean(authState?.isLoading);
  const isError = Boolean(authState?.isError);
  const message = authState?.message;

  const ordersList = useMemo(() => {
    const a = authState?.getOrderedProduct || [];
    return Array.isArray(a) ? a : [];
  }, [authState?.getOrderedProduct]);

  const [cancellingId, setCancellingId] = useState("");
  const isCancelling = Boolean(cancellingId);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
    dispatch(getOrders());
  }, [dispatch, isLoggedIn, navigate]);

  const order = useMemo(() => {
    if (!ordersList.length) return null;
    if (!orderId) return null;

    const wanted = String(orderId).trim();

    return (
      ordersList.find(
        (o) =>
          String(o?._id || "").trim() === wanted ||
          String(o?.id || "").trim() === wanted ||
          String(o?.orderId || "").trim() === wanted ||
          String(o?.orderNumber || "").trim() === wanted
      ) || null
    );
  }, [ordersList, orderId]);

  const orderItems = useMemo(() => {
    const arr = order?.orderItems || order?.items || [];
    return Array.isArray(arr) ? arr : [];
  }, [order?.orderItems, order?.items]);

  const shippingInfo = order?.shippingInfo || {};
  const paymentInfo = order?.paymentInfo || {};

  const orderNumber = useMemo(() => {
    const n =
      order?.orderNumber ||
      order?.orderId ||
      order?.reference ||
      order?._id ||
      order?.id ||
      "";
    return n ? `#${String(n).replace(/^#/, "")}` : "—";
  }, [order?.orderNumber, order?.orderId, order?.reference, order?._id, order?.id]);

  const paymentMethodLabel = useMemo(() => {
    const pm = safeText(paymentInfo?.paymentMethod || order?.paymentMethod);
    if (!pm) return "—";
    if (pm === "cashOnDelivery") return "Cash on Delivery";
    if (pm === "mobileMoney") return "Mobile Money";
    if (pm === "card") return "Card";
    if (pm === "paypal") return "PayPal";
    return pm;
  }, [paymentInfo?.paymentMethod, order?.paymentMethod]);

  const transactionId = useMemo(() => {
    return (
      safeText(paymentInfo?.transactionId) ||
      safeText(paymentInfo?.txId) ||
      safeText(order?.transactionId) ||
      "—"
    );
  }, [paymentInfo?.transactionId, paymentInfo?.txId, order?.transactionId]);

  const deliveryDate = useMemo(() => {
    const d = order?.estimatedDeliveryDate || order?.deliveryDate || order?.deliveredAt || null;
    if (!d) return "—";
    try {
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return String(d);
      return dt.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return String(d);
    }
  }, [order?.estimatedDeliveryDate, order?.deliveryDate, order?.deliveredAt]);

  const itemsTotal = useMemo(() => {
    const v = order?.itemsTotal ?? order?.totalItemsPrice ?? null;
    if (v !== null && v !== undefined && String(v).trim() !== "") return Number(v || 0);

    return orderItems.reduce((sum, x) => {
      const qty = Number(x?.quantity || x?.qty || 1) || 1;
      const unit = Number(x?.unitPrice || x?.price || 0) || 0;
      return sum + unit * qty;
    }, 0);
  }, [order?.itemsTotal, order?.totalItemsPrice, orderItems]);

  const setupFeeTotal = useMemo(() => {
    const v = order?.setupFeeTotal ?? order?.setupFee ?? 0;
    return Number(v || 0) || 0;
  }, [order?.setupFeeTotal, order?.setupFee]);

  const shipping = useMemo(() => {
    const v = order?.shippingPrice ?? order?.shipping ?? 0;
    return Number(v || 0) || 0;
  }, [order?.shippingPrice, order?.shipping]);

  const taxes = useMemo(() => {
    const v = order?.taxPrice ?? order?.taxes ?? 0;
    return Number(v || 0) || 0;
  }, [order?.taxPrice, order?.taxes]);

  const couponDiscount = useMemo(() => {
    const v = order?.couponDiscount ?? order?.discount ?? 0;
    return Number(v || 0) || 0;
  }, [order?.couponDiscount, order?.discount]);

  const total = useMemo(() => {
    const v = order?.totalPrice ?? order?.totalPriceAfterDiscount ?? null;
    if (v !== null && v !== undefined && String(v).trim() !== "") return Number(v || 0);
    return Math.max(0, itemsTotal + setupFeeTotal + shipping + taxes - couponDiscount);
  }, [
    order?.totalPrice,
    order?.totalPriceAfterDiscount,
    itemsTotal,
    setupFeeTotal,
    shipping,
    taxes,
    couponDiscount,
  ]);

  const invoiceOrderId = useMemo(() => {
    return safeText(order?._id || order?.id || orderId);
  }, [order, orderId]);

  const orderStatus = useMemo(
    () => safeText(order?.orderStatus || order?.status),
    [order?.orderStatus, order?.status]
  );

  const canCancelThisOrder = useMemo(() => {
    if (!order) return false;

    const st = orderStatus.toLowerCase();
    if (!st) return true;
    if (st === "cancelled" || st === "canceled") return false;
    if (st === "delivered") return false;

    return true;
  }, [order, orderStatus]);

  const downloadInvoice = useCallback(async () => {
    if (!invoiceOrderId) return;

    try {
      const token = localStorage.getItem("token");
      const url = `${base_url}order/${invoiceOrderId}/invoice`;

      const res = await fetch(url, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!res.ok) {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `invoice_${String(invoiceOrderId).slice(-8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch {
      const url = `${base_url}order/${invoiceOrderId}/invoice`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, [invoiceOrderId]);

  const cancelOrderNow = useCallback(async () => {
    const oid = safeText(order?._id || order?.id);
    if (!oid) return;

    const ok = window.confirm(
      "Cancel this order?\n\nThis action will mark the order as cancelled."
    );
    if (!ok) return;

    try {
      setCancellingId(oid);
      await dispatch(cancelMyOrder(oid)).unwrap();
      await dispatch(getOrders()).unwrap();
    } catch (e) {
      console.error(e);
    } finally {
      setCancellingId("");
    }
  }, [dispatch, order]);

  const showLoading = isLoading && !order;
  const showEmpty = !showLoading && !order && !isError;

  return (
    <div className="vo-page">
      <div className="vo-hero">
        <h1 className="vo-title">View Order</h1>
        <p className="vo-bread">
          Home <span>/</span> My Orders <span>/</span> View Order
        </p>
      </div>

      <div className="vo-shell">
        <div className="vo-topbar">
          <button type="button" className="vo-backBtn" onClick={() => navigate("/my-orders")}>
            ← Back to Orders
          </button>
        </div>

        {showLoading ? (
          <div className="vo-emptyCard">Loading your order...</div>
        ) : showEmpty ? (
          <div className="vo-emptyCard">
            {isError ? message || "Failed to load order." : "No order found."}
          </div>
        ) : (
          <>
            <div className="vo-summaryBar">
              <div className="vo-sItem">
                <div className="vo-sLabel">Order ID</div>
                <div className="vo-sValue">{orderNumber}</div>
              </div>

              <div className="vo-sItem">
                <div className="vo-sLabel">Payment Method</div>
                <div className="vo-sValue">{paymentMethodLabel}</div>
              </div>

              <div className="vo-sItem">
                <div className="vo-sLabel">Transaction ID</div>
                <div className="vo-sValue">{transactionId}</div>
              </div>

              <div className="vo-sItem">
                <div className="vo-sLabel">Estimated Delivery Date</div>
                <div className="vo-sValue">{deliveryDate}</div>
              </div>

              <button
                type="button"
                className="vo-actionBtn"
                onClick={downloadInvoice}
                disabled={!invoiceOrderId}
              >
                Download Invoice
              </button>

              <button
                type="button"
                className="vo-actionBtn vo-actionBtn--ghost"
                onClick={cancelOrderNow}
                disabled={!canCancelThisOrder || !order || isCancelling}
              >
                {isCancelling
                  ? "Cancelling..."
                  : orderStatus && orderStatus.toLowerCase() === "cancelled"
                  ? "Cancelled"
                  : "Cancel Order"}
              </button>
            </div>

            <div className="vo-card">
              <div className="vo-cardHead">Order Details</div>

              <div className="vo-table">
                <div className="vo-tHead">
                  <div className="vo-th">Products</div>
                  <div className="vo-th vo-th--right">Sub Total</div>
                </div>

                <div className="vo-tBody">
                  {orderItems.length ? (
                    orderItems.map((it, idx) => {
                      const qty = Number(it?.quantity || it?.qty || 1) || 1;

                      const prodObj =
                        it?.productSnapshot && typeof it.productSnapshot === "object"
                          ? it.productSnapshot
                          : it?.product && typeof it.product === "object"
                          ? it.product
                          : null;

                      const name =
                        safeText(prodObj?.title || prodObj?.name || it?.title || it?.name) ||
                        "Item";

                      const color =
                        safeText(
                          it?.color?.title || it?.color?.name || it?.colorLabel || it?.color
                        ) || "";

                      const size =
                        safeText(
                          it?.size?.title || it?.size?.name || it?.sizeLabel || it?.size
                        ) || "";

                      const unitPrice = Number(it?.unitPrice || it?.price || 0) || 0;
                      const lineTotal = unitPrice * qty;

                      const printSide = safeText(it?.printSide);
                      const printPricingTitle = safeText(it?.printPricingTitle);
                      const printUnitPrice = Number(it?.printUnitPrice || 0) || 0;

                      const img = pickOrderItemImage(it);
                      const productSlugOrId = getOrderItemSlugOrId(it);

                      return (
                        <div className="vo-row" key={it?._id || it?.id || `${name}-${idx}`}>
                          <div className="vo-prod">
                            <div
                              className="vo-img"
                              style={{ cursor: productSlugOrId ? "pointer" : "default" }}
                              onClick={() =>
                                productSlugOrId ? navigate(getProductPath(productSlugOrId)) : null
                              }
                            >
                              <img src={img} alt={name} />
                            </div>

                            <div className="vo-prodText">
                              <div
                                className="vo-name"
                                style={{ cursor: productSlugOrId ? "pointer" : "default" }}
                                onClick={() =>
                                  productSlugOrId ? navigate(getProductPath(productSlugOrId)) : null
                                }
                              >
                                {name}
                              </div>

                              <div className="vo-meta">
                                {color ? (
                                  <>
                                    Color : <span>{color}</span>
                                  </>
                                ) : null}
                                {color && size ? " | " : null}
                                {size ? (
                                  <>
                                    Size : <span>{size}</span>
                                  </>
                                ) : null}
                                {(color || size) && qty ? " | " : null}
                                {qty ? (
                                  <>
                                    Qty : <span>{qty}</span>
                                  </>
                                ) : null}
                              </div>

                              {(printPricingTitle || printSide || printUnitPrice > 0) && (
                                <div className="vo-meta vo-meta--extra">
                                  Printing : <span>{printPricingTitle || "—"}</span>
                                  {printSide ? (
                                    <>
                                      {" "}
                                      | Side : <span>{printSide}</span>
                                    </>
                                  ) : null}
                                  {printUnitPrice > 0 ? (
                                    <>
                                      {" "}
                                      | Print unit : <span>{formatUGX(printUnitPrice)}</span>
                                    </>
                                  ) : null}
                                </div>
                              )}

                              {Array.isArray(it?.uploadedFiles) && it.uploadedFiles.length ? (
                                <div className="vo-meta vo-meta--extra">
                                  Files : <span>{it.uploadedFiles.length}</span>
                                </div>
                              ) : null}

                              {it?.instruction ? (
                                <div className="vo-meta vo-meta--extra">
                                  Note : <span>{String(it.instruction)}</span>
                                </div>
                              ) : null}
                            </div>
                          </div>

                          <div className="vo-price">{formatUGX(lineTotal)}</div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="vo-noitems">No items found.</div>
                  )}
                </div>

                <div className="vo-tFoot">
                  <div className="vo-section">
                    <div className="vo-sectionTitle">Delivery</div>
                    <div className="vo-sectionBody">
                      <div>
                        <strong>Name:</strong>{" "}
                        {safeText(shippingInfo?.firstName) || safeText(shippingInfo?.lastName)
                          ? `${safeText(shippingInfo?.firstName)} ${safeText(
                              shippingInfo?.lastName
                            )}`.trim()
                          : "—"}
                      </div>
                      <div>
                        <strong>Phone:</strong> {safeText(shippingInfo?.phone) || "—"}
                      </div>
                      <div>
                        <strong>Email:</strong> {safeText(shippingInfo?.email) || "—"}
                      </div>
                      <div>
                        <strong>Address:</strong> {safeText(shippingInfo?.address) || "—"}
                        {safeText(shippingInfo?.region)
                          ? ` • ${safeText(shippingInfo.region)}`
                          : ""}
                        {safeText(shippingInfo?.subRegion)
                          ? ` • ${safeText(shippingInfo.subRegion)}`
                          : ""}
                      </div>
                      {order?.note ? (
                        <div>
                          <strong>Order note:</strong> {String(order.note)}
                        </div>
                      ) : null}
                      {orderStatus ? (
                        <div>
                          <strong>Status:</strong> <span>{orderStatus}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="vo-footRow">
                    <span>Items subtotal</span>
                    <strong>{formatUGX(itemsTotal)}</strong>
                  </div>

                  {setupFeeTotal > 0 ? (
                    <div className="vo-footRow">
                      <span>Setup fee (once)</span>
                      <strong>{formatUGX(setupFeeTotal)}</strong>
                    </div>
                  ) : null}

                  <div className="vo-footRow">
                    <span>Shipping</span>
                    <strong>{formatUGX(shipping)}</strong>
                  </div>

                  {taxes > 0 ? (
                    <div className="vo-footRow">
                      <span>Taxes</span>
                      <strong>{formatUGX(taxes)}</strong>
                    </div>
                  ) : null}

                  {couponDiscount > 0 ? (
                    <div className="vo-footRow">
                      <span>Coupon Discount</span>
                      <strong>-{formatUGX(couponDiscount)}</strong>
                    </div>
                  ) : null}

                  <div className="vo-footDivider" />

                  <div className="vo-footRow vo-footRow--total">
                    <span>Total</span>
                    <strong>{formatUGX(total)}</strong>
                  </div>

                  <div className="vo-actions">
                    <Link className="vo-shopBtn" to="/product">
                      Continue Shopping
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ViewOrders;